import { useToast } from '@chakra-ui/react'
import * as Sentry from '@sentry/react'
import type React from 'react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import DefaultModal from '../../../../../../components/DefaultModal'
import FormInput from '../../../../../../components/FormInput'
import MessageBox from '../../../../../../components/MessageBox'
import { RootState } from '../../../../../../redux/store'
import { downloadAdminSwapInvoice } from '../../../../../../utils/downloadInvoice'

interface AdminGenerateInvoiceModalProps {
  isOpen: boolean
  onClose: () => void
}

type FormData = {
  swapId: string
}

const AdminGenerateInvoiceModal: React.FC<AdminGenerateInvoiceModalProps> = ({
  isOpen,
  onClose,
}) => {
  const toast = useToast()
  const sessionToken = useSelector((state: RootState) => state.session.token)
  const [isDownloading, setIsDownloading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>()

  const resetModal = () => {
    reset()
    setError(null)
    setIsDownloading(false)
  }

  const onSubmit = async (data: FormData) => {
    setError(null)
    setIsDownloading(true)

    try {
      // Validate GUID format
      const guidRegex =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
      if (!guidRegex.test(data.swapId.trim())) {
        setError('Please enter a valid swap ID (GUID format)')
        setIsDownloading(false)
        return
      }

      await downloadAdminSwapInvoice(data.swapId.trim(), sessionToken)

      toast({
        title: 'Invoice downloaded successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })

      resetModal()
      onClose()
    } catch (e: any) {
      const errorMessage =
        e?.message ||
        'Failed to download invoice. Please check the swap ID and try again.'
      setError(errorMessage)
      Sentry.captureException(e)
      setIsDownloading(false)
    }
  }

  return (
    <DefaultModal
      isLoading={isDownloading}
      isOpen={isOpen}
      onClose={() => {
        resetModal()
        onClose()
      }}
      title="Generate Invoice for Bookkeeping"
      isLoadingText="Downloading invoice..."
      whiteBG
    >
      <form onSubmit={handleSubmit(onSubmit)} className="">
        <div className="p-5 pb-0 space-y-3">
          <FormInput
            label="Swap ID"
            placeholder="Enter swap ID (GUID)"
            inputMode="text"
            registeredForm={register('swapId', {
              required: 'Swap ID is required',
            })}
            error={errors?.swapId}
          />
        </div>

        {error && (
          <div className="p-5 pt-3">
            <MessageBox type="warning" title={error} />
          </div>
        )}

        <div className="p-5 pt-0">
          <button
            type="submit"
            className="button mt-10"
            disabled={isDownloading}
          >
            Download Invoice
          </button>
        </div>
      </form>
    </DefaultModal>
  )
}

export default AdminGenerateInvoiceModal
